import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Users,
  Gamepad2,
  Mic,
  Shield,
  Zap,
  Check,
  Github,
  MessageCircle,
  Star,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { logEvent } from "@/lib/analytics";
import botImage from "@/assets/icon.webp";
import AnimatedSection from "@/components/AnimatedSection";

const WHATSAPP_NUMBER = "919028230554";
const GITHUB_LINK = "https://github.com/stylish00killer";

const createWhatsAppLink = (pack) => {
  const message = encodeURIComponent(`I want to buy the Alice Bot ${pack} Pack`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
};

// Hero Section
const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center py-20 overflow-hidden" data-testid="hero-section">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-main/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slow" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            <div
              className="transition-all duration-700 ease-out"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(20px)",
                transitionDelay: "100ms"
              }}
            >
              <Badge className="mb-6 bg-emerald-main/10 text-emerald-light border-emerald-main/30 hover:bg-emerald-main/20">
                <Zap className="w-3 h-3 mr-1" /> Instant Delivery
              </Badge>
            </div>

            <h1
              className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 transition-all duration-700 ease-out text-white"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(30px)",
                transitionDelay: "200ms"
              }}
            >
              Get Alice Bot — Ultimate WhatsApp Group Automation
            </h1>

            <p
              className="text-lg md:text-xl text-gray-400 mb-8 max-w-xl transition-all duration-700 ease-out"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(30px)",
                transitionDelay: "300ms"
              }}
            >
              Powerful moderation, auto-tagging, games, tools & more — delivered instantly.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 transition-all duration-700 ease-out"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(30px)",
                transitionDelay: "400ms"
              }}
            >
              <a href={createWhatsAppLink("Starter")} target="_blank" rel="noopener noreferrer" onClick={() => logEvent('Contact', 'Click', 'WhatsApp Hero')}>
                <Button
                  data-testid="hero-whatsapp-cta"
                  className="w-full sm:w-auto bg-whatsapp hover:bg-[#128C7E] text-white font-bold rounded-full px-8 py-6 text-lg flex items-center justify-center gap-2 shadow-lg shadow-whatsapp/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-whatsapp/40"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contact on WhatsApp to Buy Now
                </Button>
              </a>
              <a href={GITHUB_LINK} target="_blank" rel="noopener noreferrer" onClick={() => logEvent('Engagement', 'Click', 'GitHub Hero')}>
                <Button
                  data-testid="hero-github-cta"
                  variant="outline"
                  className="w-full sm:w-auto border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-8 py-6 text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105"
                >
                  <Github className="w-5 h-5" />
                  View on GitHub
                </Button>
              </a>
            </div>

            <div
              className="mt-8 flex items-center gap-6 text-gray-500 text-sm transition-all duration-700 ease-out"
              style={{
                opacity: loaded ? 1 : 0,
                transitionDelay: "500ms"
              }}
            >
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-main" />
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-main" />
                <span>Instant Setup</span>
              </div>
            </div>
          </div>

          {/* Right - Bot Image */}
          <div
            className="relative flex justify-center lg:justify-end transition-all duration-1000 ease-out"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateX(0) scale(1)" : "translateX(50px) scale(0.9)",
              transitionDelay: "300ms"
            }}
          >
            <div className="relative w-[280px] sm:w-[350px] lg:w-[400px] animate-float">
              <div className="absolute inset-0 bg-emerald-main/20 blur-[60px] rounded-full" />
              <img
                src={botImage}
                alt="Alice Bot Mascot - WhatsApp Automation Assistant"
                className="relative z-10 w-full h-auto rounded-3xl border border-white/10 shadow-2xl"
                width="400"
                height="400"
                fetchPriority="high"
                loading="eager"
                data-testid="hero-mascot-image"
              />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <a
          href="#features"
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center transition-all duration-700 cursor-pointer"
          style={{
            opacity: loaded ? 1 : 0,
            transitionDelay: "800ms"
          }}
          aria-label="Scroll to features"
        >
          <span className="text-gray-500 text-sm mb-2">Scroll to explore</span>
          <ChevronDown className="w-6 h-6 text-emerald-main animate-bounce" />
        </a>
      </div>
    </section>
  );
};

// Features Section
const features = [
  { icon: Users, title: "Auto Tag All", desc: "Mention <strong>everyone</strong> in a group <strong>instantly</strong>" },
  { icon: Shield, title: "Admin Tools & Controls", desc: "<strong>Kick, warn, mute</strong> with simple commands" },
  { icon: Gamepad2, title: "Games (Tic-Tac-Toe etc.)", desc: "Keep your group <strong>engaged</strong> with fun games" },
  { icon: Mic, title: "Text-to-Speech & Stickers", desc: "Convert <strong>text to voice</strong> and create <strong>stickers</strong>" },
  { icon: Shield, title: "Anti-Link Protection", desc: "<strong>Auto-delete</strong> unauthorized links instantly" },
  { icon: Zap, title: "24/7 Uptime", desc: "<strong>Always online</strong>, always working for you" },
];

const FeaturesSection = () => (
  <section id="features" className="py-24 md:py-32 relative" data-testid="features-section">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-main/5 rounded-full blur-[100px] pointer-events-none" />

    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <AnimatedSection className="text-center mb-16">
        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
          What Alice Bot Does
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Powerful features to automate and protect your WhatsApp groups
        </p>
      </AnimatedSection>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <AnimatedSection key={index} delay={index * 100}>
            <div
              className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-emerald-main/50 transition-all duration-300 group relative overflow-hidden h-full"
              data-testid={`feature-card-${index}`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-main/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-emerald-main/10 flex items-center justify-center mb-5 group-hover:bg-emerald-main/20 transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-emerald-light" />
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            </div>
          </AnimatedSection>
        ))}
      </div>
    </div>
  </section>
);

// Pricing Section
const pricingPacks = [
  {
    name: "Starter",
    inr: "₹1,499",
    usd: "$19.99",
    features: ["Basic Bot Commands", "Auto Tag All", "Anti-Link Protection", "Community Support", "Ideal for small groups"],
    popular: false
  },
  {
    name: "Pro",
    inr: "₹2,999",
    usd: "$39.99",
    features: ["All Starter Features", "Games & Entertainment", "Text-to-Speech", "Sticker Creator", "Priority Support"],
    popular: true
  },
  {
    name: "Elite",
    inr: "₹4,999",
    usd: "$59.99",
    features: ["All Pro Features", "Advanced Admin Tools", "Custom Commands", "White-label Option", "24/7 Priority Support"],
    popular: false
  },
];

const PricingSection = () => {
  const [currency, setCurrency] = useState("inr");

  return (
    <section id="pricing" className="py-24 md:py-32 relative" data-testid="pricing-section">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-main/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <AnimatedSection className="text-center mb-12">
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Choose Your Pack
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            All prices include tax & charges
          </p>

          {/* Currency Toggle */}
          <div className="inline-flex items-center bg-white/5 rounded-full p-1 border border-white/10">
            <button
              onClick={() => setCurrency("inr")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${currency === "inr" ? "bg-emerald-main text-black" : "text-gray-400 hover:text-white"}`}
              data-testid="currency-inr-btn"
            >
              INR ₹
            </button>
            <button
              onClick={() => setCurrency("usd")}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${currency === "usd" ? "bg-emerald-main text-black" : "text-gray-400 hover:text-white"}`}
              data-testid="currency-usd-btn"
            >
              USD $
            </button>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {pricingPacks.map((pack, index) => (
            <AnimatedSection key={index} delay={index * 150}>
              <div
                className={`relative rounded-3xl p-8 flex flex-col h-full transition-all duration-300 hover:-translate-y-2 ${pack.popular
                  ? "bg-black border-2 border-emerald-main shadow-[0_0_40px_rgba(16,185,129,0.15)] scale-105 z-10"
                  : "bg-black border border-white/10 hover:border-emerald-main/50"
                  }`}
                data-testid={`pricing-card-${pack.name.toLowerCase()}`}
              >
                {pack.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-main text-black font-bold px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-outfit text-2xl font-bold mb-2">{pack.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="font-outfit text-4xl font-bold text-gradient">
                      {currency === "inr" ? pack.inr : pack.usd}
                    </span>
                    <span className="text-gray-500 text-sm">incl. tax</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {pack.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-emerald-main flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a href={createWhatsAppLink(pack.name)} target="_blank" rel="noopener noreferrer">
                  <Button
                    className={`w-full rounded-full py-6 font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] ${pack.popular
                      ? "bg-whatsapp hover:bg-[#128C7E] text-white shadow-lg shadow-whatsapp/30"
                      : "bg-white/10 hover:bg-white/20 text-white"
                      }`}
                    data-testid={`buy-${pack.name.toLowerCase()}-btn`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    Buy on WhatsApp
                  </Button>
                </a>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Custom Pack */}
        <AnimatedSection delay={500} className="mt-12">
          <div
            className="bg-gradient-to-r from-white/5 to-transparent border border-white/10 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 hover:border-emerald-main/30 transition-colors duration-300"
            data-testid="custom-pack-section"
          >
            <div>
              <h3 className="font-outfit text-2xl font-bold mb-2">Need a Custom Solution?</h3>
              <p className="text-gray-400">Custom deployment, features & priority support tailored to your needs.</p>
            </div>
            <a href={createWhatsAppLink("Custom")} target="_blank" rel="noopener noreferrer">
              <Button
                className="bg-emerald-main hover:bg-emerald-light text-black font-bold rounded-full px-8 py-6 flex items-center gap-2 shadow-lg shadow-emerald-main/30 transition-all duration-300 hover:scale-105"
                data-testid="custom-pack-cta"
              >
                <MessageCircle className="w-5 h-5" />
                Get Custom Quote
              </Button>
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// How It Works Section
const steps = [
  { num: "01", title: "Choose Your Pack", desc: "Select the pack that fits your needs" },
  { num: "02", title: "Contact on WhatsApp", desc: "Message us to confirm your order" },
  { num: "03", title: "Complete Payment", desc: "Pay securely via preferred method" },
  { num: "04", title: "Receive Bot + Setup", desc: "Get instant delivery with setup support" },
];

const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 md:py-32 relative bg-white/[0.02]" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <AnimatedSection className="text-center mb-16">
        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
          How It Works
        </h2>
        <p className="text-gray-400 text-lg">
          Get started in 4 simple steps
        </p>
      </AnimatedSection>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((step, index) => (
          <AnimatedSection key={index} delay={index * 150}>
            <div className="relative text-center" data-testid={`step-${index + 1}`}>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[60%] w-full h-[2px] bg-gradient-to-r from-emerald-main/50 to-transparent" />
              )}
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-emerald-main/10 border border-emerald-main/30 flex items-center justify-center mx-auto mb-4 transition-all duration-300 hover:bg-emerald-main/20 hover:scale-110">
                  <span className="font-outfit text-2xl font-bold text-emerald-main">{step.num}</span>
                </div>
                <h3 className="font-outfit text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.desc}</p>
              </div>
            </div>
          </AnimatedSection>
        ))}
      </div>
    </div>
  </section>
);

// Testimonials Section
const testimonials = [
  { name: "Rahul S.", role: "Group Admin", text: "Alice Bot transformed our group management — highly recommended!" },
  { name: "Priya M.", role: "Community Manager", text: "The auto-tag feature alone is worth every penny. Amazing bot!" },
  { name: "Alex K.", role: "Gaming Community Lead", text: "Games and moderation tools keep our 500+ member group engaged daily." },
  { name: "Sarah J.", role: "Business Owner", text: "Professional support and instant delivery. Couldn't ask for more!" },
];

const TestimonialsSection = () => (
  <section id="testimonials" className="py-24 md:py-32 overflow-hidden" data-testid="testimonials-section">
    <AnimatedSection className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
      <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-center">
        What Our Users Say
      </h2>
    </AnimatedSection>

    {/* Marquee Container */}
    <div className="relative flex overflow-hidden group" style={{ '--marquee-duration': '30s' }}>
      <div className="absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />

      {/* First set of testimonials */}
      <div className="flex gap-6 animate-marquee flex-shrink-0 py-4 px-3 group-hover:[animation-play-state:paused]">
        {testimonials.map((testimonial, index) => (
          <div
            key={`t1-${index}`}
            className="flex-shrink-0 w-[320px] bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-emerald-main/30 transition-colors duration-300"
            data-testid={`testimonial-1-${index}`}
          >
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-emerald-main text-emerald-main" />
              ))}
            </div>
            <p className="text-gray-300 mb-4">"{testimonial.text}"</p>
            <div>
              <p className="font-semibold">{testimonial.name}</p>
              <p className="text-sm text-gray-500">{testimonial.role}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Second set of testimonials (identical clone for seamless loop) */}
      <div className="flex gap-6 animate-marquee flex-shrink-0 py-4 px-3 group-hover:[animation-play-state:paused]" aria-hidden="true">
        {testimonials.map((testimonial, index) => (
          <div
            key={`t2-${index}`}
            className="flex-shrink-0 w-[320px] bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-emerald-main/30 transition-colors duration-300"
          >
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-emerald-main text-emerald-main" />
              ))}
            </div>
            <p className="text-gray-300 mb-4">"{testimonial.text}"</p>
            <div>
              <p className="font-semibold">{testimonial.name}</p>
              <p className="text-sm text-gray-500">{testimonial.role}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100); // Small delay to ensure rendering
      }
    }
  }, [location]);

  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <HowItWorksSection />
      <TestimonialsSection />
    </>
  );
};

export default Home;
